import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Modal } from 'antd'
import { ModalProps } from 'antd/lib'

import {
  useInvestigationsServiceGetInvestigationDemandListEndpointKey,
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useTreatmentServiceCreateNewbornInvestigationEndpoint,
  useTreatmentServiceGetListNewbornInvestigationEndpointKey,
  useTreatmentServiceGetListPatientNewbornsEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  NewbornInvestigationPeriod,
  NewbornPlanInvestigationItemDto
} from 'src/api/requests'
import { message, SaveConfirm } from 'src/components'
import { InvestigationCategories } from 'src/features/persons/components'
import { parseCategories } from 'src/features/persons/utils'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  data?: {
    id?: string
    data?: NewbornPlanInvestigationItemDto
  }
  onCancel: () => void
} & ModalProps

export const PregnancyNewBornInvestigationsModal = ({
  data,
  onCancel,
  ...props
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const { mutate, isPending } =
    useTreatmentServiceCreateNewbornInvestigationEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetListNewbornInvestigationEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [
            useInvestigationsServiceGetInvestigationDemandListEndpointKey
          ]
        })
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetListPatientNewbornsEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
        })
        message.success(t('consultations.investigation_created_successfully'))
        onCancel()
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const categories = parseCategories({ categories: values.categories })

    if (categories.length === 0) {
      return message.error(t('laboratory_classifiers.select_category_error'))
    }

    mutate({
      id: String(data?.id),
      requestBody: {
        newbornPeriod:
          data?.data?.newbornPeriod ??
          NewbornInvestigationPeriod.AT_FORTY_EIGHT_HOURS,
        notes: values.notes,
        categories: categories
      }
    })
  }

  return (
    <Modal
      width={800}
      maskClosable={false}
      open
      centered
      onCancel={onCancel}
      title={t('consultations.new_investigation_request')}
      footer={null}
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto'
        }
      }}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false} disabled={false}>
        <Flex vertical gap={16}>
          <Flex vertical gap={24}>
            <InvestigationCategories form={form} />

            <Form.Item name="notes" label={t('common.comments')}>
              <Input.TextArea rows={5} />
            </Form.Item>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t('common.confirm')}?`}
                onConfirm={onSubmit}
                okButtonProps={{ loading: isPending }}
              >
                <Button type="primary">{t('common.confirm')}</Button>
              </SaveConfirm>
            </Flex>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
